import { Fragment, useEffect, useState } from 'react';

import { Combobox } from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleOutline } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { AlertBox, Label, Spinner } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useField } from 'formik';
import { useFormContext, useWatch } from 'react-hook-form';

import { CustomerStateCompanyType } from '../../api/lending/edge';
import { InlineButton } from '../../Buyer/components/InlineButton/InlineButton';
import { normaliseDate } from '../../Buyer/Signup/stateNormalisation';
import { getFormattedDate } from '../../Buyer/Signup/steps/BusinessDetails/BusinessDetails';
import { iwocapayTelDisplay, iwocapayTelLink } from '../../constants.json';
import { extractError } from '../../utils/ReactHookForm';
import { formatDate } from '../Input/formatDate';
import { HookFormInput, Input } from '../Input/Input';
import { InputError } from '../InputError/InputError';
import SearchIcon from '../svg/MagnifyingGlass.svg';
import { fetchGetMirroredCompanySearch } from './utils/fetchGetMirroredCompaniesHouse';

function useFieldValue(id: string) {
  const [{ value }, , { setValue }] = useField(id);

  return [value, setValue];
}

const MANUAL_TRADING_DATE_ID = 'company.manualTradingDate';
const MANUAL_INPUT_ID = 'company.manualInput';

export const CompanyInput = ({
  disabled,
  allowManual,
  customerType = 'buyer',
}: {
  disabled?: boolean;
  allowManual?: boolean;
  customerType?: 'buyer' | 'seller';
}) => {
  const [isManualTradingDateInput, setManualTradingDateInput] = useFieldValue(
    MANUAL_TRADING_DATE_ID,
  );
  const [isManualInput, setIsManualInput] = useFieldValue(MANUAL_INPUT_ID);
  const [companyType] = useFieldValue('companyType');
  const [companyName] = useFieldValue('companyName');
  const [tradingStartDate] = useFieldValue('tradingStartDate');
  const [companyNumber] = useFieldValue('companyNumber');

  const isSoleTrader = getIsSoleTrader(companyType);

  useEffect(() => {
    if (companyType) {
      tracking.ampli.companyTypeSelected({ company_type: companyType });
    }
  }, [companyType]);

  if (isSoleTrader) {
    return <SoleTraderInput customerType={customerType} />;
  }

  if (isManualInput) {
    return <ManualInput />;
  }

  return (
    <div>
      <Label
        className="mx-0 mb-s text-m font-med"
        htmlFor="companyName"
        id="companyName__label"
      >
        Your company / partnership name
      </Label>
      <CompaniesSearchInput disabled={Boolean(disabled)} />
      {allowManual ? (
        <InlineButton
          className="mx-0 mb-r mt-s !text-s"
          onClick={() => setIsManualInput(true)}
        >
          Enter company details manually
        </InlineButton>
      ) : null}
      {!isManualTradingDateInput ? (
        companyName && (
          <div className="mx-0 mt-s">
            <p className="fs-mask m-0 text-s font-reg text-black-90">
              {companyName} ({companyNumber})
            </p>
            {tradingStartDate ? (
              <p className="fs-mask m-0 text-s font-reg text-black-90">
                Trading start date: {getFormattedDate(tradingStartDate)}
              </p>
            ) : null}
            <InlineButton
              onClick={() =>
                setManualTradingDateInput(!isManualTradingDateInput)
              }
              type="button"
            >
              Change trading start date
            </InlineButton>
          </div>
        )
      ) : (
        <>
          <Input
            className="mt-l w-full max-w-[400px]"
            label="Company number"
            name="companyNumber"
            disabled
            type="text"
          />
          <Input
            type="date-input"
            className="mt-l w-full max-w-[400px]"
            label="Trading start date (DD/MM/YYYY)"
            labelDescriptionText="This is the date your business started trading. This may be different to your company incorporation date."
            placeholder="DD/MM/YYYY"
            name="tradingStartDate"
          />
        </>
      )}
    </div>
  );
};

const CompaniesSearchInput = ({ disabled }: { disabled: boolean }) => {
  const [
    { value: companyName },
    { error, touched },
    { setValue: setCompanyName },
  ] = useField('companyName');
  const [, setCompanyNumberValue] = useFieldValue('companyNumber');
  const [, setTradingStartDate] = useFieldValue('tradingStartDate');
  const [, setManualTradingDateInput] = useFieldValue(MANUAL_TRADING_DATE_ID);
  const [isManualInput] = useFieldValue(MANUAL_INPUT_ID);

  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [companyOptions, setCompanyOptions] = useState<
    GetCompanySearchResponse['data'][number][]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleOptionChange = (
    company: GetCompanySearchResponse['data'][number],
  ) => {
    if (!company.registration_date) {
      return setManualTradingDateInput(true);
    }
    setManualTradingDateInput(false);
  };

  useEffect(() => {
    const companySearch = setTimeout(async () => {
      setNumberOfCallsLoading((prevNumber) => prevNumber + 1);
      if (searchQuery) {
        try {
          const response = await fetchGetMirroredCompanySearch(searchQuery);
          if (response.data) {
            setCompanyOptions(response.data);
          }
        } catch {
          setCompanyOptions([]);
        }
      } else {
        setCompanyOptions([]);
      }
      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    }, 200);

    return () => {
      clearTimeout(companySearch);
    };
  }, [searchQuery]);

  const renderOptions = () => {
    if (numberOfCallsLoading > 0) {
      return (
        <div className="flex justify-center p-s">
          <Spinner dataTestId="company-search-spinner" />
        </div>
      );
    }

    return (
      <>
        {companyOptions.length > 0 ? (
          companyOptions.map((company, index) => (
            <Combobox.Option
              className={({ active }) =>
                clsx('px-l py-s hover:cursor-pointer hover:bg-secondary-97', {
                  'cursor-pointer bg-secondary-97': active,
                })
              }
              key={index}
              value={company}
              as="li"
            >
              {company.name} | {company.number}
            </Combobox.Option>
          ))
        ) : (
          <p className="m-0 px-l py-s">No results</p>
        )}
      </>
    );
  };

  return (
    <Combobox
      value={companyName}
      onChange={async (company: GetCompanySearchResponse['data'][number]) => {
        await setCompanyName(company.name);
        setCompanyNumberValue(company.number);
        setTradingStartDate(
          company.registration_date
            ? normaliseDate(company.registration_date)
            : undefined,
        );

        handleOptionChange(company);
      }}
    >
      <div className="relative flex max-w-[400px] flex-col">
        <img className="absolute left-l mt-r" src={SearchIcon} alt="" />
        <Combobox.Input
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          as={Fragment}
        >
          <input
            name="companyName"
            aria-labelledby="companyName__label"
            placeholder="Company name..."
            className="fs-mask rounded-s border border-structure-80 bg-white py-r pl-3xl pr-l font-sans text-[14px] focus:outline focus:outline-2 focus:outline-primary-60"
            disabled={disabled}
            type="text"
          />
        </Combobox.Input>
        <InputError error={error} isVisible={!isManualInput && touched} />
        {searchQuery && (
          <Combobox.Options
            className="fs-mask absolute top-[45px] z-[2] m-0 max-h-[200px] w-full list-none overflow-y-auto rounded-s border border-divider bg-white p-0 text-grey-60"
            as="ul"
          >
            {renderOptions()}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

const SoleTraderInput = ({
  customerType = 'buyer',
}: {
  customerType: 'buyer' | 'seller';
}) => {
  const [companyType] = useFieldValue('companyType');

  const displayCompanyType =
    companyType === 'ordinary_partnership'
      ? 'Ordinary Partnerships'
      : 'Sole Traders';

  if (customerType === 'seller') {
    return (
      <AlertBox className="flex gap-m text-secondary-40" variant="connect">
        <IconInfoCircleOutline className="text-2xl" />
        <div className="py-2xs text-s">
          <h1 className="m-0 text-s font-bold">
            Unfortunately we don't work with {displayCompanyType} at the moment
          </h1>
          Get in touch on{' '}
          <a className="text-primary-40" href={iwocapayTelLink}>
            {iwocapayTelDisplay}
          </a>{' '}
          if you aren't sure what this means for your business.
        </div>
      </AlertBox>
    );
  }

  return (
    <>
      <div>
        <Input
          name="tradingName"
          label="Trading name"
          placeholder="Enter trading name..."
          className="max-w-[400px]"
          type="text"
        />
      </div>
      <div>
        <Input
          label="Trading start date (DD/MM/YYYY)"
          labelDescriptionText="This is the date your business started trading."
          placeholder="DD/MM/YYYY"
          name="tradingStartDate"
          formatter={formatDate}
          className="max-w-[400px]"
          type="text"
        />
      </div>
    </>
  );
};

const ManualInput = () => {
  return (
    <div>
      <Input
        label="Your company / partnership name"
        name="companyName"
        type="text"
      />
      <Input label="Company number" name="companyNumber" type="text" />
    </div>
  );
};

const SOLE_TRADER_TYPES: Array<CustomerStateCompanyType> = [
  'sole_trader',
  'ordinary_partnership',
  'other',
];
function getIsSoleTrader(companyType?: CustomerStateCompanyType) {
  if (!companyType) return false;

  return SOLE_TRADER_TYPES.includes(companyType);
}

function useHookFormFieldValue(id: string) {
  const { control, setValue } = useFormContext();
  const value = useWatch({ control, name: id });
  const set = (next: unknown) =>
    setValue(id, next, { shouldValidate: true, shouldDirty: true });
  return [value, set] as const;
}

/** @public — temporarily unused; consumers migrating from formik will adopt this in follow-up MRs (IP-3162) */
export const HookFormCompanyInput = ({
  disabled,
  allowManual,
  customerType = 'buyer',
}: {
  disabled?: boolean;
  allowManual?: boolean;
  customerType?: 'buyer' | 'seller';
}) => {
  const [isManualTradingDateInput, setManualTradingDateInput] =
    useHookFormFieldValue(MANUAL_TRADING_DATE_ID);
  const [isManualInput, setIsManualInput] =
    useHookFormFieldValue(MANUAL_INPUT_ID);
  const [companyType] = useHookFormFieldValue('companyType');
  const [companyName] = useHookFormFieldValue('companyName');
  const [tradingStartDate] = useHookFormFieldValue('tradingStartDate');
  const [companyNumber] = useHookFormFieldValue('companyNumber');

  const isSoleTrader = getIsSoleTrader(
    companyType as CustomerStateCompanyType | undefined,
  );

  useEffect(() => {
    if (companyType) {
      tracking.ampli.companyTypeSelected({ company_type: companyType });
    }
  }, [companyType]);

  if (isSoleTrader) {
    return <HookFormSoleTraderInput customerType={customerType} />;
  }

  if (isManualInput) {
    return <HookFormManualInput />;
  }

  return (
    <div>
      <Label
        className="mx-0 mb-s text-m font-med"
        htmlFor="companyName"
        id="companyName__label"
      >
        Your company / partnership name
      </Label>
      <HookFormCompaniesSearchInput disabled={Boolean(disabled)} />
      {allowManual ? (
        <InlineButton
          className="mx-0 mb-r mt-s !text-s"
          onClick={() => setIsManualInput(true)}
        >
          Enter company details manually
        </InlineButton>
      ) : null}
      {!isManualTradingDateInput ? (
        companyName && (
          <div className="mx-0 mt-s">
            <p className="fs-mask m-0 text-s font-reg text-black-90">
              {companyName} ({companyNumber})
            </p>
            {tradingStartDate ? (
              <p className="fs-mask m-0 text-s font-reg text-black-90">
                Trading start date: {getFormattedDate(tradingStartDate)}
              </p>
            ) : null}
            <InlineButton
              onClick={() =>
                setManualTradingDateInput(!isManualTradingDateInput)
              }
              type="button"
            >
              Change trading start date
            </InlineButton>
          </div>
        )
      ) : (
        <>
          <HookFormInput
            className="mt-l w-full max-w-[400px]"
            label="Company number"
            name="companyNumber"
            disabled
            type="text"
          />
          <HookFormInput
            type="date-input"
            className="mt-l w-full max-w-[400px]"
            label="Trading start date (DD/MM/YYYY)"
            labelDescriptionText="This is the date your business started trading. This may be different to your company incorporation date."
            placeholder="DD/MM/YYYY"
            name="tradingStartDate"
          />
        </>
      )}
    </div>
  );
};

const HookFormCompaniesSearchInput = ({ disabled }: { disabled: boolean }) => {
  const { control, setValue, formState } = useFormContext();
  const companyName = useWatch({ control, name: 'companyName' });
  const companyNameError = extractError(formState.errors['companyName']);
  const isCompanyNameTouched = formState.touchedFields['companyName'];
  const [, setCompanyNumberValue] = useHookFormFieldValue('companyNumber');
  const [, setTradingStartDate] = useHookFormFieldValue('tradingStartDate');
  const [, setManualTradingDateInput] = useHookFormFieldValue(
    MANUAL_TRADING_DATE_ID,
  );
  const [isManualInput] = useHookFormFieldValue(MANUAL_INPUT_ID);

  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [companyOptions, setCompanyOptions] = useState<
    GetCompanySearchResponse['data'][number][]
  >([]);
  const [searchQuery, setSearchQuery] = useState('');

  const handleOptionChange = (
    company: GetCompanySearchResponse['data'][number],
  ) => {
    if (!company.registration_date) {
      return setManualTradingDateInput(true);
    }
    setManualTradingDateInput(false);
  };

  useEffect(() => {
    const companySearch = setTimeout(async () => {
      setNumberOfCallsLoading((prevNumber) => prevNumber + 1);
      if (searchQuery) {
        try {
          const response = await fetchGetMirroredCompanySearch(searchQuery);
          if (response.data) {
            setCompanyOptions(response.data);
          }
        } catch {
          setCompanyOptions([]);
        }
      } else {
        setCompanyOptions([]);
      }
      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    }, 200);

    return () => {
      clearTimeout(companySearch);
    };
  }, [searchQuery]);

  const renderOptions = () => {
    if (numberOfCallsLoading > 0) {
      return (
        <div className="flex justify-center p-s">
          <Spinner dataTestId="company-search-spinner" />
        </div>
      );
    }

    return (
      <>
        {companyOptions.length > 0 ? (
          companyOptions.map((company, index) => (
            <Combobox.Option
              className={({ active }) =>
                clsx('px-l py-s hover:cursor-pointer hover:bg-secondary-97', {
                  'cursor-pointer bg-secondary-97': active,
                })
              }
              key={index}
              value={company}
              as="li"
            >
              {company.name} | {company.number}
            </Combobox.Option>
          ))
        ) : (
          <p className="m-0 px-l py-s">No results</p>
        )}
      </>
    );
  };

  return (
    <Combobox
      value={companyName}
      onChange={(company: GetCompanySearchResponse['data'][number]) => {
        setValue('companyName', company.name, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
        setCompanyNumberValue(company.number);
        setTradingStartDate(
          company.registration_date
            ? normaliseDate(company.registration_date)
            : undefined,
        );

        handleOptionChange(company);
      }}
    >
      <div className="relative flex max-w-[400px] flex-col">
        <img className="absolute left-l mt-r" src={SearchIcon} alt="" />
        <Combobox.Input
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          as={Fragment}
        >
          <input
            name="companyName"
            aria-labelledby="companyName__label"
            placeholder="Company name..."
            className="fs-mask rounded-s border border-structure-80 bg-white py-r pl-3xl pr-l font-sans text-[14px] focus:outline focus:outline-2 focus:outline-primary-60"
            disabled={disabled}
            type="text"
          />
        </Combobox.Input>
        <InputError
          error={companyNameError}
          isVisible={!isManualInput && Boolean(isCompanyNameTouched)}
        />
        {searchQuery && (
          <Combobox.Options
            className="fs-mask absolute top-[45px] z-[2] m-0 max-h-[200px] w-full list-none overflow-y-auto rounded-s border border-divider bg-white p-0 text-grey-60"
            as="ul"
          >
            {renderOptions()}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

const HookFormSoleTraderInput = ({
  customerType = 'buyer',
}: {
  customerType: 'buyer' | 'seller';
}) => {
  const [companyType] = useHookFormFieldValue('companyType');

  const displayCompanyType =
    companyType === 'ordinary_partnership'
      ? 'Ordinary Partnerships'
      : 'Sole Traders';

  if (customerType === 'seller') {
    return (
      <AlertBox className="flex gap-m text-secondary-40" variant="connect">
        <IconInfoCircleOutline className="text-2xl" />
        <div className="py-2xs text-s">
          <h1 className="m-0 text-s font-bold">
            Unfortunately we don't work with {displayCompanyType} at the moment
          </h1>
          Get in touch on{' '}
          <a className="text-primary-40" href={iwocapayTelLink}>
            {iwocapayTelDisplay}
          </a>{' '}
          if you aren't sure what this means for your business.
        </div>
      </AlertBox>
    );
  }

  return (
    <>
      <div>
        <HookFormInput
          name="tradingName"
          label="Trading name"
          placeholder="Enter trading name..."
          className="max-w-[400px]"
          type="text"
        />
      </div>
      <div>
        <HookFormInput
          label="Trading start date (DD/MM/YYYY)"
          labelDescriptionText="This is the date your business started trading."
          placeholder="DD/MM/YYYY"
          name="tradingStartDate"
          formatter={formatDate}
          className="max-w-[400px]"
          type="text"
        />
      </div>
    </>
  );
};

const HookFormManualInput = () => {
  return (
    <div>
      <HookFormInput
        label="Your company / partnership name"
        name="companyName"
        type="text"
      />
      <HookFormInput label="Company number" name="companyNumber" type="text" />
    </div>
  );
};
